import { useContext, useState } from 'react';
import { css, styled } from 'styled-components';
import { Modal } from 'react-bootstrap';
import { Button } from '@vivaahealth/design-system';

import * as api from '../../../../api';

import { type ChatConversation } from '../../../../common';

import { ReactComponent as ThumbUpSvg } from '../../../../assets/thumbs-up.svg';
import { ReactComponent as CloseIcon } from '../../../../assets/close_icon.svg';
import { fadeIn } from '../../../../styles/animation';
import { ConfigurationContext } from '../../../../context/configurationContext';

interface Props {
  message: ChatConversation;
  showTextAnimation: boolean;
  showDesktopView: boolean;
}

const Feedback: React.FC<Props> = ({
  message,
  showTextAnimation,
  showDesktopView,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [comment, setComment] = useState('');
  const [feedbackSelected, setFeedbackSelected] =
    useState<api.PatientFeedback.FeedbackType | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const configurationContext = useContext(ConfigurationContext);

  const submitFeedback = async () => {
    if (!feedbackSelected) return;
    if (!('answer' in message)) return;
    if (!message.chat_message_id) return;
    if (!configurationContext?.organizationId) return;
    if (!configurationContext.id) return;
    setIsSubmitting(true);
    try {
      await api.client.Assistant.createMessageFeedback({
        messageId: message.chat_message_id,
        feedback: feedbackSelected,
        comment,
        configurationId: configurationContext.id,
      });
      setIsSubmitted(true);
    } catch (error) {}
    setIsModalOpen(false);
    setIsSubmitting(false);
  };

  const renderModal = () => {
    return (
      <FeedbackModal
        className="modal"
        show={isModalOpen}
        centered
        onHide={() => {
          setFeedbackSelected(null);
          setComment('');
          setIsModalOpen(false);
        }}
      >
        <form
          onSubmit={(event) => {
            event.preventDefault();
            submitFeedback();
          }}
        >
          <ModalHeader>
            <ModalTitle>Provide additional feedback</ModalTitle>
            <CloseButton type="button">
              <CloseIcon
                onClick={() => {
                  setFeedbackSelected(null);
                  setComment('');
                  setIsModalOpen(false);
                }}
              />
            </CloseButton>
          </ModalHeader>

          <Modal.Body>
            <ModalBodyLabel htmlFor="comment">
              What do you{' '}
              {feedbackSelected === api.PatientFeedback.FeedbackType.Negative
                ? 'dislike'
                : 'like'}{' '}
              about the response? <OptionalLabel>(optional)</OptionalLabel>
            </ModalBodyLabel>
            <ModalBodyTextArea
              id="comment"
              value={comment}
              onChange={(event) => setComment(event.target.value)}
            ></ModalBodyTextArea>
          </Modal.Body>

          <Modal.Footer>
            <Button type="submit" disabled={isSubmitting}>
              Submit feedback
            </Button>
          </Modal.Footer>
        </form>
      </FeedbackModal>
    );
  };

  return (
    <AnimatedFeedback
      $showTextAnimation={showTextAnimation}
      $showDesktopView={showDesktopView}
    >
      {renderModal()}
      {'answer' in message &&
      (message.done || message.error === api.Assistant.MessageError.Timeout) ? (
        <FeedbackContainer>
          <ThumbIcon
            type="button"
            $isDown={false}
            onClick={() => {
              if (isSubmitted) return;
              setIsModalOpen(true);
              setFeedbackSelected(api.PatientFeedback.FeedbackType.Positive);
            }}
            $selected={
              feedbackSelected === api.PatientFeedback.FeedbackType.Positive
            }
            $visible={
              !feedbackSelected ||
              feedbackSelected === api.PatientFeedback.FeedbackType.Positive
            }
            disabled={isSubmitted}
          >
            <ThumbUpSvg />
          </ThumbIcon>
          <ThumbIcon
            type="button"
            $isDown={true}
            onClick={() => {
              if (isSubmitted) return;
              setIsModalOpen(true);
              setFeedbackSelected(api.PatientFeedback.FeedbackType.Negative);
            }}
            data-target="#commentModal"
            data-toggle="modal"
            $selected={
              feedbackSelected === api.PatientFeedback.FeedbackType.Negative
            }
            $visible={
              !feedbackSelected ||
              feedbackSelected === api.PatientFeedback.FeedbackType.Negative
            }
            disabled={isSubmitted}
          >
            <ThumbUpSvg />
          </ThumbIcon>
        </FeedbackContainer>
      ) : (
        <Placeholder></Placeholder>
      )}
    </AnimatedFeedback>
  );
};

const AnimatedFeedback = styled.div.attrs<{
  $showTextAnimation: boolean;
  $showDesktopView: boolean;
}>((props) => ({
  $showTextAnimation: props.$showTextAnimation ?? false,
  $showDesktopView: props.$showDesktopView ?? false,
}))`
  opacity: ${(props) => (props.$showTextAnimation ? 0 : 1)};
  animation: ${(props) =>
    props.$showTextAnimation
      ? css`
          ${fadeIn} .6s ease-in-out forwards
        `
      : 'none'};
  display: ${(props) => (props.$showDesktopView ? 'block' : 'none')};
  @media (max-width: 680px) {
    display: ${(props) => (!props.$showDesktopView ? 'block' : 'none')};
  }
`;

const FeedbackContainer = styled.div`
  margin-left: 1.5rem;
  display: flex;
  align-items: start;
  animation: ${fadeIn} 0.6s ease;

  @media (max-width: 680px) {
    margin-left: 0;
    margin-top: 24px;
    margin-bottom: 1rem;
  }
`;

const Placeholder = styled.div`
  width: 46px;
  margin-left: 1.5rem;

  @media (max-width: 680px) {
    display: none;
  }
`;

const ThumbIcon = styled.button.attrs<{
  $isDown: boolean;
  $selected: boolean;
  $visible: boolean;
}>((props) => ({
  $isDown: props.$isDown ?? false,
  $selected: props.$selected ?? false,
  $visible: props.$visible ?? false,
}))`
  border: none;
  background: ${(props) => (props.$selected ? '#EDEDED' : 'transparent')};
  padding: 0px;
  margin-right: ${(props) => (props.$isDown ? '0' : '0.5rem')};
  transform: ${(props) => (props.$isDown ? 'rotate(180deg)' : 'rotate(0deg)')};
  height: 24px;
  width: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  opacity: ${(props) => (props.$visible ? 1 : 0)};
  pointer-events: ${(props) => (props.$visible ? 'auto' : 'none')};

  svg {
    overflow: visible;
    height: 16px;
    width: 16px;
    color: ${(props) =>
      props.$selected
        ? props.$isDown
          ? 'var(--color-state-danger)'
          : 'var(--color-state-success)'
        : 'var(--color-hint)'};
  }

  &:hover {
    svg {
      color: ${(props) =>
        props.$isDown
          ? 'var(--color-state-danger)'
          : 'var(--color-state-success)'};
    }
  }
`;

const ModalHeader = styled(Modal.Header)`
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
`;

const ModalTitle = styled(Modal.Title)`
  font-size: 16px;
  font-weight: 600;
`;

const ModalBodyLabel = styled.label`
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const ModalBodyTextArea = styled.textarea`
  width: 100%;
  border-color: var(--border-color);
  min-height: 87px;
  border-radius: 4px;
  font-size: 14px;
  padding: 10px;
`;

const CloseButton = styled.button`
  border: none;
  background-color: transparent;
`;

const OptionalLabel = styled.span`
  font-weight: normal;
`;

const FeedbackModal = styled(Modal)`
  z-index: 100005;
`;

export default Feedback;
